import { useState } from 'react'
import { useProfile, useInventory, useAllLogs, dayNumberFromStartDate, useAiPlan } from '../lib/hooks'
import { getEffectiveDayPlan, TOTAL_DAYS, formatDayDate } from '../data/plan'
import { GROCERY_ITEMS, SUPPLEMENT_ITEMS, PREP_AHEAD } from '../data/groceries'
import { getEffectiveMeals, getEffectiveGroceryList } from '../lib/mealOverrides'

function formatAddedDate(iso) {
  if (!iso) return 'Never added'
  const d = new Date(iso + 'T00:00:00')
  return `Added ${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
}

function StockRow({ item, stock, needQty, onAdd }) {
  const [amount, setAmount] = useState('')
  const qty = stock?.qty > 0 ? stock.qty : 0
  const short = needQty != null && qty < needQty
  const deficit = short ? needQty - qty : 0

  const submitAdd = () => {
    const val = parseFloat(amount)
    if (!Number.isFinite(val) || val <= 0) return
    onAdd(val)
    setAmount('')
  }

  return (
    <div className="stock-row">
      <div className="stock-row-top">
        <span className="stock-row-name">{item.name}</span>
        <span className={`stock-row-qty ${qty <= 0 ? 'is-empty' : ''}`}>{qty}{item.unit}</span>
      </div>
      <div className="stock-row-meta">
        <span>{formatAddedDate(stock?.lastAdded)}</span>
        {needQty != null && <span>NEEDS {needQty}{item.unit} FOR TOMORROW</span>}
        {short && <span className="stock-need-tag">SHORT {deficit}{item.unit}</span>}
      </div>
      <div className="stock-add-row">
        <input
          type="number"
          inputMode="decimal"
          className="stock-add-qty"
          placeholder={`ADD ${item.unit.toUpperCase()}`}
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && submitAdd()}
        />
        <button className="stock-add-btn" onClick={submitAdd}>ADD</button>
      </div>
    </div>
  )
}

export default function Groceries() {
  const { profile } = useProfile()
  const { inventory, addStock } = useInventory()
  const { logs } = useAllLogs()
  const { aiPlan } = useAiPlan()
  const currentDay = profile?.startDate ? dayNumberFromStartDate(profile.startDate) : 1
  const tomorrowDay = currentDay + 1

  const hasTomorrow = tomorrowDay <= TOTAL_DAYS
  const tomorrowPlan = hasTomorrow ? getEffectiveDayPlan(tomorrowDay, aiPlan) : null
  const tomorrowEffectiveMeals = tomorrowPlan ? getEffectiveMeals(tomorrowPlan.meals, logs[String(tomorrowDay)]?.customMeals) : []
  const tomorrowIngredients = tomorrowPlan ? getEffectiveGroceryList(tomorrowEffectiveMeals) : []
  const tomorrowNeeded = tomorrowIngredients.filter((g) => (inventory[g.key]?.qty || 0) < g.qty)
  const prepNote = tomorrowPlan ? PREP_AHEAD[tomorrowPlan.menuNumber] : null

  const categories = [...new Set(Object.entries(GROCERY_ITEMS).map(([, v]) => v.category))]
  const needMap = Object.fromEntries(tomorrowIngredients.map((g) => [g.key, g.qty]))

  return (
    <div className="section">
      <span className="plan-eyebrow">● KEEP STOCKED</span>
      <h1 className="display-heading" style={{ fontSize: 'clamp(30px, 8vw, 80px)', margin: '16px 0 32px' }}>Groceries</h1>

      {prepNote && (
        <div className="reminder-banner" style={{ borderColor: '#4a3a14', background: '#151109' }}>
          <div className="reminder-banner-left">
            <span className="reminder-dot" />
            <span className="reminder-text">{prepNote}</span>
          </div>
        </div>
      )}

      {tomorrowPlan && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-head">
            <div className="card-title-group">
              <span className="card-index">01</span>
              <h2 className="card-title">Tomorrow · Day {String(tomorrowDay).padStart(2, '0')}</h2>
            </div>
            <span className="card-count">{tomorrowNeeded.length ? `${tomorrowNeeded.length} SHORT` : 'ALL SET'}</span>
          </div>
          <p className="card-subtitle">{formatDayDate(tomorrowDay)} · {tomorrowPlan.workoutLabel} · Menu {tomorrowPlan.menuNumber} · {tomorrowPlan.menuName}</p>
          <div style={{ marginTop: 12 }}>
            {tomorrowIngredients.map((g) => (
              <StockRow key={g.key} item={g} stock={inventory[g.key]} needQty={g.qty} onAdd={(amt) => addStock(g.key, amt, g.unit)} />
            ))}
          </div>
        </div>
      )}

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-head">
          <div className="card-title-group">
            <span className="card-index">02</span>
            <h2 className="card-title">Supplement stock</h2>
          </div>
        </div>
        <div style={{ marginTop: 4 }}>
          {Object.entries(SUPPLEMENT_ITEMS).map(([key, item]) => (
            <StockRow key={key} item={item} stock={inventory[key]} onAdd={(amt) => addStock(key, amt, item.unit)} />
          ))}
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <div className="card-title-group">
            <span className="card-index">03</span>
            <h2 className="card-title">Pantry inventory</h2>
          </div>
        </div>
        {categories.map((cat) => (
          <div key={cat} style={{ marginTop: 16 }}>
            <p className="grocery-category">{cat.toUpperCase()}</p>
            {Object.entries(GROCERY_ITEMS)
              .filter(([, item]) => item.category === cat)
              .map(([key, item]) => (
                <StockRow key={key} item={item} stock={inventory[key]} needQty={needMap[key]} onAdd={(amt) => addStock(key, amt, item.unit)} />
              ))}
          </div>
        ))}
      </div>
    </div>
  )
}
